import { FC, useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './AboutCountry.module.scss';

import CountryTabs from './CountryTabs/CountryTabs';
import { DestinationCountry } from '../../../../../types/trip.types';
import { useGetDestinationCountryDetailsQuery } from '../../../../../store/services/tripsApi';

interface AboutCountryProps {
  countries: DestinationCountry[];
  onShowCountryDetails?: (countryData: any) => void;
}

const AboutCountry: FC<AboutCountryProps> = ({ countries, onShowCountryDetails }) => {
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const onShowCountryDetailsRef = useRef(onShowCountryDetails);

  useEffect(() => {
    onShowCountryDetailsRef.current = onShowCountryDetails;
  }, [onShowCountryDetails]);

  useEffect(() => {
    if (countries.length > 0 && selectedCountryId === null) {
      setSelectedCountryId(countries[0].id);
    }
  }, [countries, selectedCountryId]);

  const {
    data: countryData,
    isLoading,
    error,
  } = useGetDestinationCountryDetailsQuery(selectedCountryId!, {
    skip: selectedCountryId === null,
  });

  useEffect(() => {
    if (countryData && countryData.about_country && onShowCountryDetailsRef.current) {
      onShowCountryDetailsRef.current(countryData);
    }
  }, [countryData]);

  const handleCountryChange = (countryId: number) => {
    setSelectedCountryId(countryId);
  };

  if (countries.length === 0) {
    return (
      <Box className={styles.aboutCountry}>
        <Box className={styles.emptyState}>
          <Typography
            variant="h7"
            className={styles.emptyTitle}
          >
            No Countries Available
          </Typography>
          <Typography
            variant="h10"
            className={styles.emptyText}
          >
            Add destination countries to your trip to view information about them.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.aboutCountry}>
      <CountryTabs
        countries={countries}
        selectedCountryId={selectedCountryId || countries[0].id}
        onCountryChange={handleCountryChange}
      />

      <Box className={styles.contentContainer}>
        {isLoading && (
          <Box className={styles.loadingState}>
            <Typography variant="h10">Loading country information...</Typography>
          </Box>
        )}

        {error && (
          <Box className={styles.errorState}>
            <Typography
              variant="h10"
              color="error"
            >
              Failed to load country information. Please try again.
            </Typography>
          </Box>
        )}

        {countryData && !isLoading && !error && countryData.about_country && (
          <Box className={styles.countryMainContent}>
            {countryData.about_country.general_info && (
              <Box className={styles.generalInfo}>
                <Typography
                  variant="h11"
                  className={styles.generalText}
                >
                  {countryData.about_country.general_info}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {countryData && !isLoading && !error && !countryData.about_country && (
          <Box className={styles.errorState}>
            <Typography
              variant="h10"
              color="textSecondary"
            >
              Country information is currently unavailable.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AboutCountry;
